import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import {
  addNostoQuickAddToCart,
  bindTileSwatchSwap,
  fetchNostoRecommendations,
  fetchThemeTileData,
  nostoSizeOptionIndex,
  productHandleFromHref,
  stripTrailingZeroCents,
} from '@theme/nosto-recommendations';

/** The product card's desktop/flyout split (`quickAddDesktopQuery` in assets/quick-add.js); keep in step. */
const sizeRowQuery = matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');

/**
 * AMP Back in Stock drawer + subscribe via window.BIS.create.
 * @extends {Component}
 */
class BackInStockComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Promise<void> | null} */
  #similarLoadPromise = null;

  /** Bumped whenever the drawer is re-pointed at another product, so an in-flight
   * similar-products load for the previous one can't write its cards into the new one. */
  #similarToken = 0;

  /** @type {ResizeObserver | null} */
  #similarResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Same scope product-form.js listens on. `.shopify-section` alone misses the quick-add
    // modal, which rescues this component out of the fetched PDP into a <dialog> with no
    // section around it - so the CTA never woke up there and an out-of-stock size had to be
    // punted to the PDP instead. The shared page-level drawer matches nothing here, which
    // is right: it is driven by openFor(), not by the page's own variant picker.
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );
    this.querySelector('.back-in-stock__trigger')?.addEventListener('pointerdown', this.#onTriggerPointerDown, {
      signal,
    });

    // Whichever way this drawer is opened, this is the moment its product row has to be
    // right - and the one hook every path goes through, including the quick-add modal's
    // Notify Me CTA, which morph may leave connected across cards.
    this.querySelector('dialog-component')?.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, {
      signal,
    });

    this.#maybeAutoOpenFromUrl();
    this.querySelector('[data-bis-scroll="prev"]')?.addEventListener('click', this.#onSimilarScroll, { signal });
    this.querySelector('[data-bis-scroll="next"]')?.addEventListener('click', this.#onSimilarScroll, { signal });
    this.addEventListener('click', this.#onSimilarCardClick, { signal });

    // The list has no width while the drawer is closed, so re-check the arrows once it lays out.
    const list = this.querySelector('[data-bis-similar-list]');
    if (list instanceof HTMLElement) {
      list.addEventListener('scroll', () => this.#syncSimilarArrows(list), { passive: true, signal });
      bindTileSwatchSwap(list, '.back-in-stock__similar-card', this.#swapSimilarColour, signal);
      this.#similarResizeObserver = new ResizeObserver(() => this.#syncSimilarArrows(list));
      this.#similarResizeObserver.observe(list);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#similarResizeObserver?.disconnect();
    this.#similarResizeObserver = null;
  }

  #onTriggerPointerDown = () => {
    this.#loadSimilarProducts();
  };

  /**
   * Auto-opens the drawer when arriving from a PLP "notify me" link
   * (?notify=1). SSR already gates `hidden` on the resolved variant's
   * availability, so this only fires when the size is genuinely still
   * out of stock.
   */
  #maybeAutoOpenFromUrl() {
    // The shared drawer is bound to no product until a card opens it, and it sits on
    // every page alongside the PDP's own instance - only that one owns the URL param.
    if (this.hidden || this.hasAttribute('data-shared')) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('notify') !== '1') return;

    this.querySelector('dialog-component')?.showDialog?.();

    params.delete('notify');
    const search = params.toString();
    history.replaceState(
      history.state,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
  }

  /**
   * Points the shared drawer at one card's out-of-stock size and opens it, so the shopper
   * subscribes where the card is instead of being sent to the PDP. The PDP's own instance
   * is bound to its product at render time and never goes through here.
   *
   * @param {{ productId: string, variantId: string, size?: string, title?: string,
   *   image?: string, url?: string }} options
   */
  openFor({ productId, variantId, size = '', title = '', image = '', url = '' }) {
    if (!productId || !variantId) return;

    const productChanged = this.dataset.productId !== String(productId);
    this.dataset.productId = String(productId);
    this.dataset.variantId = String(variantId);

    this.#updateProduct(title, image, url);
    this.#updateCopy(size);
    this.#resetStatus();
    if (productChanged) this.#resetSimilar();

    this.querySelector('dialog-component')?.showDialog?.();

    // Same product, another size: the cards are already there, they just have to be
    // re-filtered to the size now being asked about.
    const list = this.querySelector('[data-bis-similar-list]');
    if (list instanceof HTMLElement && list.dataset.loaded === 'true') {
      this.#applySizeFilter(list);
      return;
    }

    this.#loadSimilarProducts();
  }

  /**
   * Inside the quick-add modal this drawer covers the modal completely on mobile, so it
   * has to name the product itself. The card that opened the modal left its context on the
   * dialog (`#stashNotifyContext` in assets/quick-add.js) - read that rather than scraping
   * the modal's own markup, which differs between breakpoints. On the real PDP there is no
   * such ancestor and the row stays as it was: hidden, since the page already names the
   * product. The shared drawer has been filled in by `openFor()` before it gets here.
   */
  #onDialogOpen = () => {
    const modal = this.closest('quick-add-dialog');
    if (!(modal instanceof HTMLElement)) return;

    this.#updateProduct(modal.dataset.notifyTitle ?? '', modal.dataset.notifyImage ?? '', modal.dataset.notifyUrl ?? '');
  };

  /**
   * Names the product in the drawer, from whichever source knows it.
   *
   * @param {string} title
   * @param {string} image
   * @param {string} url
   */
  #updateProduct(title, image, url) {
    const root = this.querySelector('[data-bis-product]');
    if (!(root instanceof HTMLElement)) return;

    const titleEl = this.querySelector('[data-bis-product-title]');
    if (titleEl instanceof HTMLElement) titleEl.textContent = title;

    const img = this.querySelector('[data-bis-product-image]');
    if (img instanceof HTMLImageElement) {
      // Cleared rather than left stale, so a product with no image can't inherit the
      // previous one's thumbnail.
      if (image) img.src = image;
      else img.removeAttribute('src');
      img.hidden = !image;
    }

    if (root instanceof HTMLAnchorElement) {
      if (url) root.href = url;
      else root.removeAttribute('href');
    }

    root.hidden = !title;
  }

  /**
   * Drops the previous product's similar products so the next open starts empty rather
   * than showing another product's recommendations while its own load is in flight.
   */
  #resetSimilar() {
    this.#similarToken++;
    this.#similarLoadPromise = null;

    const arrows = this.querySelector('[data-bis-similar-arrows]');
    if (arrows instanceof HTMLElement) arrows.hidden = true;

    const list = this.querySelector('[data-bis-similar-list]');
    if (!(list instanceof HTMLElement)) return;
    delete list.dataset.loaded;
    list.replaceChildren();
  }

  /**
   * Same Product Recommendations API as the PDP "You may also like" block.
   */
  async #loadSimilarProducts() {
    const list = this.querySelector('[data-bis-similar-list]');
    if (!(list instanceof HTMLElement) || list.dataset.loaded === 'true') return;
    if (this.#similarLoadPromise) return this.#similarLoadPromise;

    const productId = this.dataset.productId;
    if (!productId) return;

    const token = this.#similarToken;

    this.#similarLoadPromise = (async () => {
      try {
        const cards = await this.#cardsFromNosto(productId);
        if (token !== this.#similarToken || !cards.length) return;

        list.replaceChildren(...cards);
        list.dataset.loaded = 'true';
        this.#wireSimilarCards(list);
        this.#applySizeFilter(list);
        this.#applyThemeTileData(list);
      } catch {
      } finally {
        if (token === this.#similarToken) this.#similarLoadPromise = null;
      }
    })();

    return this.#similarLoadPromise;
  }

  /**
   * @param {string} productId
   * @returns {Promise<HTMLElement[]>}
   */
  async #cardsFromNosto(productId) {
    const placement = this.dataset.nostoPlacement || 'categorypage-nosto-1';
    const variantId = this.dataset.variantId;
    const { [placement]: products = [] } = await fetchNostoRecommendations([placement], 'product', {
      products: [{ product_id: String(productId), ...(variantId ? { sku_id: String(variantId) } : {}) }],
    });
    const size = (this.dataset.variantSize || '').trim();

    return products
      .map((product) => this.#fromNostoProduct(product))
      .filter((product) => product != null && String(product.id) !== String(productId))
      .slice(0, 8)
      .map((product) => this.#buildSimilarCard(product, size))
      .filter((card) => card instanceof HTMLElement);
  }

  /**
   * @param {Record<string, any>} product
   * @returns {Record<string, any> | null}
   */
  #fromNostoProduct(product) {
    const title = String(product?.name || '').trim();
    const handle = String(product?.handle || '').trim();
    const url = String(product?.url || '').trim();
    if (!title || (!url && !handle)) return null;

    /** @type {Record<string, any>[]} */
    const variants = [];
    for (const sku of Array.isArray(product.skus) ? product.skus : []) {
      const variant = this.#fromNostoSku(sku);
      if (variant) variants.push(variant);
    }

    const images = [product.image_url, ...(product.alternate_image_urls || [])].filter(Boolean).map(String);
    const price = Number(product.price);

    return {
      id: product.product_id,
      title,
      handle,
      url,
      price: Number.isFinite(price) ? Math.round(price * 100) : undefined,
      available: variants.some((variant) => variant.available),
      images,
      variants,
      options: ['Size', 'Colour'],
    };
  }

  /**
   * @param {Record<string, any>} sku
   * @returns {Record<string, any> | null}
   */
  #fromNostoSku(sku) {
    if (!sku?.id) return null;

    // Nosto may put custom_fields on `available` instead of a boolean.
    const raw = sku.available;
    const available = raw === true || raw === 'true' || (raw != null && typeof raw === 'object');
    const fields = {
      ...(typeof sku.custom_fields === 'object' && sku.custom_fields ? sku.custom_fields : {}),
      ...(typeof raw === 'object' && raw ? raw : {}),
    };

    let size = String(fields['Size:Taille'] || fields.Size || '').trim();
    let colour = String(fields['Colour:Couleur'] || fields.Colour || fields.Color || '').trim();
    if ((!size || !colour) && typeof sku.name === 'string') {
      const [fromNameSize, fromNameColour] = sku.name.split('/').map((part) => part.trim());
      size ||= fromNameSize || '';
      colour ||= fromNameColour || '';
    }

    return {
      id: sku.id,
      available,
      options: [size, colour],
      option1: size,
      option2: colour,
    };
  }

  /**
   * @param {Record<string, any>} product
   * @param {string} size
   * @returns {Record<string, any> | null}
   */
  #variantForSize(product, size) {
    const needle = size.trim().toLowerCase();
    if (!needle) return null;

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return (
      variants.find((variant) => {
        if (!variant?.available) return false;
        return String(variant.option1 || variant.options?.[0] || '')
          .trim()
          .toLowerCase() === needle;
      }) || null
    );
  }

  /**
   * @param {Record<string, any>} product
   * @returns {string}
   */
  #productImageSrc(product) {
    const featured = product?.featured_image;
    if (typeof featured === 'string' && featured) return featured;
    if (featured && typeof featured === 'object' && featured.src) return String(featured.src);

    const first = product?.images?.[0];
    if (typeof first === 'string' && first) return first;
    if (first && typeof first === 'object' && first.src) return String(first.src);
    return '';
  }

  /**
   * @param {Record<string, any>} product
   * @param {string} [preferredSize]
   * @returns {HTMLElement | null}
   */
  #buildSimilarCard(product, preferredSize = '') {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const href = product?.url || (product?.handle ? `${root}products/${product.handle}` : '');
      if (!href || !product?.title) return null;

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const sizeVariant = preferredSize ? this.#variantForSize(product, preferredSize) : null;
      const variant = sizeVariant || variants.find((item) => item?.available) || variants[0];
      const sizePairs = variants
        .filter((item) => item?.available && item?.id && item?.option1)
        .map((item) => `${item.option1}:${item.id}`);

      const card = document.createElement('div');
      card.className = 'back-in-stock__similar-card';
      if (variant?.id) card.dataset.variantId = String(variant.id);
      if (sizePairs.length) card.dataset.bisSizes = sizePairs.join('|');
      const sizeRow = this.#sizeRow(variants.map((item) => ({ ...item, size: item?.option1 })));
      if (sizeRow.length) card.dataset.bisSizeRow = JSON.stringify(sizeRow);
      if (!product.available && !sizeVariant) card.setAttribute('data-unavailable', '');

      const link = document.createElement('a');
      link.className = 'back-in-stock__similar-link';
      link.href = href;

      const imageSrc = this.#productImageSrc(product);
      if (imageSrc) {
        const img = document.createElement('img');
        img.className = 'back-in-stock__similar-image';
        img.loading = 'lazy';
        img.alt = product.title;
        img.src = this.#sizedImageUrl(imageSrc, 400);
        link.append(img);
      }

      const name = document.createElement('span');
      name.className = 'back-in-stock__similar-name';
      name.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'back-in-stock__similar-price';
      price.textContent = this.#formatMoney(product.price);

      link.append(name, price);

      /* Outside the link: swatches are links too. */
      const colours = document.createElement('div');
      colours.className = 'back-in-stock__similar-colors';

      card.append(link, colours);
      return card;
    } catch {
      return null;
    }
  }

  /**
   * Nosto's name carries the SKU and it sends no colour family, so both come from the theme.
   * @param {HTMLElement} list
   */
  #applyThemeTileData(list) {
    for (const card of list.querySelectorAll('.back-in-stock__similar-card')) {
      const href = card.querySelector('.back-in-stock__similar-link')?.getAttribute('href') ?? null;
      const handle = productHandleFromHref(href);
      if (!handle) continue;

      card.setAttribute('data-theme-tile-data', 'pending');
      fetchThemeTileData(handle).then((data) => {
        const name = card.querySelector('.back-in-stock__similar-name');
        if (data?.title && name) name.textContent = data.title;
        const colours = card.querySelector('.back-in-stock__similar-colors');
        if (data?.swatches && colours) colours.replaceChildren(data.swatches.cloneNode(true));
        card.removeAttribute('data-theme-tile-data');
      });
    }
  }

  /**
   * Switches the card to the clicked sibling colour.
   * @param {HTMLElement} card
   * @param {import('@theme/nosto-recommendations').TileData} data
   * @returns {boolean}
   */
  #swapSimilarColour = (card, data) => {
    const product = data.product;
    const list = card.parentElement;
    if (!product?.variants?.length || !list) return false;

    const sizeIndex = nostoSizeOptionIndex(product.options ?? []);
    const variants = product.variants.map((variant) => ({
      id: variant.id,
      available: variant.available,
      option1: sizeIndex >= 0 ? variant.options?.[sizeIndex] : '',
    }));
    const next = this.#buildSimilarCard(
      {
        id: product.id,
        title: data.title || product.name,
        handle: product.handle,
        url: product.url,
        price: product.price,
        available: variants.some((variant) => variant.available),
        images: product.image?.src ? [product.image.src] : [],
        variants,
      },
      (this.dataset.variantSize || '').trim()
    );
    if (!next) return false;

    /* In place, so the old image stays up until the new one loads. */
    this.#closeCardSizes(card);
    for (const key of ['variantId', 'bisSizes', 'bisSizeRow']) {
      if (next.dataset[key]) card.dataset[key] = next.dataset[key];
      else delete card.dataset[key];
    }
    card.toggleAttribute('data-unavailable', next.hasAttribute('data-unavailable'));

    const link = card.querySelector('.back-in-stock__similar-link');
    if (link instanceof HTMLAnchorElement) link.href = product.url;
    const image = card.querySelector('.back-in-stock__similar-image');
    if (image instanceof HTMLImageElement && product.image?.src) {
      image.src = product.image.src;
      image.alt = data.title || product.name;
    }
    for (const selector of ['.back-in-stock__similar-name', '.back-in-stock__similar-price']) {
      const el = card.querySelector(selector);
      if (el) el.textContent = next.querySelector(selector)?.textContent ?? '';
    }
    const hadFocus = card.contains(document.activeElement);
    const colours = card.querySelector('.back-in-stock__similar-colors');
    if (data.swatches) colours?.replaceChildren(data.swatches.cloneNode(true));

    const stale = card.querySelectorAll('[data-bis-card-actions], .back-in-stock__similar-sizes, quick-add-component');
    stale.forEach((el) => el.remove());
    this.#wireSimilarCards(list);
    if (hadFocus) card.querySelector('.color-swatch__link.is-selected')?.focus({ preventScroll: true });
    return true;
  };

  /**
   * @param {number | string | undefined} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    let text = '';
    if (typeof window.Shopify?.formatMoney === 'function') {
      try {
        text = window.Shopify.formatMoney(value);
      } catch {}
    }
    if (!text) text = `$${(value / 100).toFixed(2)}`;

    /* Both sources always give two decimals, but every price on this site drops a
       whole-dollar `.00` - these cards are the theme's own markup, so the rule applies
       here as much as anywhere. */
    return stripTrailingZeroCents(text);
  }

  /**
   * @param {HTMLElement} list
   */
  #applySizeFilter(list) {
    const size = (this.dataset.variantSize || '').trim().toLowerCase();
    const cards = Array.from(list.querySelectorAll('.back-in-stock__similar-card')).filter(
      (card) => card instanceof HTMLElement
    );
    if (!cards.length) return;

    for (const card of cards) card.hidden = false;

    if (!size) {
      this.#syncSimilarArrows(list);
      return;
    }

    /** @type {HTMLElement[]} */
    const matches = [];
    for (const card of cards) {
      const variantId = this.#variantIdFromCardSizes(card, size);
      if (!variantId) continue;

      matches.push(card);
      card.dataset.variantId = variantId;
      card.removeAttribute('data-unavailable');
      const atcBtn = card.querySelector('[data-bis-atc]');
      if (atcBtn instanceof HTMLButtonElement) {
        atcBtn.dataset.variantId = variantId;
        atcBtn.disabled = false;
      }
    }

    if (matches.length) {
      for (const card of cards) card.hidden = !matches.includes(card);
    }

    this.#syncSimilarArrows(list);
  }

  /**
   * @param {HTMLElement} card
   * @param {string} size
   * @returns {string}
   */
  #variantIdFromCardSizes(card, size) {
    for (const pair of (card.dataset.bisSizes || '').split('|')) {
      const [label, id] = pair.split(':');
      if (label?.trim().toLowerCase() === size && id) return id;
    }
    return '';
  }

  /** @param {HTMLElement} list */
  #syncSimilarArrows(list) {
    const arrows = this.querySelector('[data-bis-similar-arrows]');
    if (!(arrows instanceof HTMLElement)) return;
    const visible = list.querySelectorAll('.back-in-stock__similar-card:not([hidden])').length;
    arrows.hidden = visible <= 3;

    const maxScroll = list.scrollWidth - list.clientWidth;
    const prev = arrows.querySelector('[data-bis-scroll="prev"]');
    const next = arrows.querySelector('[data-bis-scroll="next"]');
    if (prev instanceof HTMLButtonElement) prev.disabled = list.scrollLeft <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = list.scrollLeft >= maxScroll - 1;
  }

  /**
   * @param {HTMLElement} list
   */
  #wireSimilarCards(list) {
    for (const card of list.querySelectorAll('.back-in-stock__similar-card')) {
      if (!(card instanceof HTMLElement)) continue;

      if (!card.querySelector('[data-bis-card-actions]')) {
        const actions = document.createElement('div');
        actions.className = 'back-in-stock__similar-actions';
        actions.dataset.bisCardActions = '';

        const atcBtn = document.createElement('button');
        atcBtn.type = 'button';
        atcBtn.className = 'back-in-stock__similar-atc';
        atcBtn.dataset.bisAtc = '';
        atcBtn.setAttribute('aria-label', 'Add to cart');

        const variantId = card.dataset.variantId;
        if (variantId) atcBtn.dataset.variantId = variantId;
        if (card.hasAttribute('data-unavailable') || !variantId) atcBtn.disabled = true;

        actions.append(atcBtn);
        card.append(actions);
      }

      this.#backfillCardVariant(card).catch(() => {
        /* leave the add-to-cart button disabled */
      });
    }
  }

  /**
   * Nosto can omit SKUs; fall back to the product's own first available variant.
   * @param {HTMLElement} card
   */
  async #backfillCardVariant(card) {
    const atcBtn = card.querySelector('[data-bis-atc]');
    if (!(atcBtn instanceof HTMLButtonElement) || atcBtn.dataset.variantId) return;

    const link = card.querySelector('.back-in-stock__similar-link');
    if (!(link instanceof HTMLAnchorElement)) return;

    const handle = this.#productHandleFromUrl(link.href);
    if (!handle) return;

    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return;
    const product = await response.json();
    if (!card.isConnected || atcBtn.dataset.variantId) return;

    const variant = product?.variants?.find((item) => item?.available) ?? product?.variants?.[0];
    if (variant?.id) {
      atcBtn.dataset.variantId = String(variant.id);
      atcBtn.disabled = !variant.available;
    }
  }

  /** @param {Event} event */
  #onSimilarCardClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const card = target.closest('.back-in-stock__similar-card');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return;

    const size = target.closest('.back-in-stock__similar-sizes .quick-add__size');
    if (size instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      if (size.disabled) return;
      addNostoQuickAddToCart(size)?.then(() => this.#closeCardSizes(card));
      return;
    }

    if (target.closest('[data-bis-sizes-close]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#closeCardSizes(card, true);
      return;
    }

    const atcBtn = target.closest('[data-bis-atc]');
    if (atcBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      if (sizeRowQuery.matches) {
        this.#openCardSizes(card, atcBtn);
      } else {
        this.#openQuickAddFlyout(card, event);
      }
    }
  };

  /**
   * One entry per size, in the product's own order; a size is available if any of its variants is.
   * @param {Array<{ size?: string, id?: string | number, available?: boolean }>} variants
   * @returns {Array<{ size: string, id: string, available: boolean }>}
   */
  #sizeRow(variants) {
    /** @type {Map<string, { size: string, id: string, available: boolean }>} */
    const sizes = new Map();
    for (const variant of variants) {
      const size = String(variant?.size ?? '').trim();
      if (!size || !variant?.id) continue;
      const existing = sizes.get(size);
      if (existing?.available) continue;
      sizes.set(size, { size, id: String(variant.id), available: Boolean(variant.available) });
    }
    return [...sizes.values()];
  }

  /**
   * The card's sizes: from Nosto's SKUs when it sent them, otherwise from the product's own JSON.
   * @param {HTMLElement} card
   */
  async #cardSizes(card) {
    try {
      const cached = JSON.parse(card.dataset.bisSizeRow || '[]');
      if (Array.isArray(cached) && cached.length) return cached;
    } catch {}

    const link = card.querySelector('.back-in-stock__similar-link');
    const handle = link instanceof HTMLAnchorElement ? this.#productHandleFromUrl(link.href) : '';
    if (!handle) return [];

    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return [];
    const product = await response.json();
    const sizeIndex = nostoSizeOptionIndex(Array.isArray(product?.options) ? product.options : []);
    if (sizeIndex < 0) return [];

    const sizes = this.#sizeRow(
      (product.variants || []).map((variant) => ({ ...variant, size: variant?.options?.[sizeIndex] }))
    );
    if (sizes.length) card.dataset.bisSizeRow = JSON.stringify(sizes);
    return sizes;
  }

  /**
   * Desktop: the PLP card's inline size row (snippets/quick-add.liquid) in place of the "+".
   * @param {HTMLElement} card
   * @param {HTMLButtonElement} atcBtn
   */
  async #openCardSizes(card, atcBtn) {
    let sizes = [];
    try {
      sizes = await this.#cardSizes(card);
    } catch {}
    if (!card.isConnected) return;

    // No size option to pick from: the "+" adds the card's own variant, as the PLP's single-size "+" does.
    if (sizes.length <= 1) {
      const variantId = sizes[0]?.available === false ? '' : sizes[0]?.id || atcBtn.dataset.variantId;
      if (!variantId) return;
      atcBtn.dataset.variant = variantId;
      addNostoQuickAddToCart(atcBtn);
      return;
    }

    let row = card.querySelector('.back-in-stock__similar-sizes');
    if (!(row instanceof HTMLElement)) {
      row = this.#buildSizeRow(card, sizes);
      card.append(row);
    }

    // Once per card, so a colour swap can't stack them.
    if (!card.hasAttribute('data-bis-sizes-bound')) {
      card.setAttribute('data-bis-sizes-bound', '');
      const signal = this.#abortController?.signal;
      card.addEventListener('pointerleave', () => this.#closeCardSizes(card), { signal });
      card.addEventListener(
        'focusout',
        (event) => {
          const next = event.relatedTarget;
          if (next instanceof Node && !card.contains(next)) this.#closeCardSizes(card);
        },
        { signal }
      );
      // Escape closes the row before it reaches the drawer's dialog, which would close the whole drawer.
      card.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape' || !card.hasAttribute('data-sizes-open')) return;
          event.preventDefault();
          event.stopPropagation();
          this.#closeCardSizes(card, true);
        },
        { signal }
      );
    }

    row.hidden = false;
    card.dataset.sizesOpen = 'true';
    atcBtn.setAttribute('aria-expanded', 'true');
    row.querySelector('.quick-add__size:not(:disabled)')?.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLElement} card
   * @param {Array<{ size: string, id: string, available: boolean }>} sizes
   */
  #buildSizeRow(card, sizes) {
    const title = card.querySelector('.back-in-stock__similar-name')?.textContent ?? '';
    const addLabel = this.dataset.sizeAddLabel || 'Add';
    const unavailableTemplate = this.dataset.sizeUnavailableTemplate || '__SIZE__';

    const row = document.createElement('div');
    row.className = 'back-in-stock__similar-sizes';
    row.hidden = true;

    const list = document.createElement('div');
    list.className = 'quick-add__sizes';

    for (const { size, id, available } of sizes) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'quick-add__size';
      if (available) {
        chip.dataset.variant = id;
        chip.setAttribute('aria-label', `${addLabel} ${title}, ${size}`);
        const label = document.createElement('span');
        label.className = 'quick-add__size-label';
        label.textContent = size;
        chip.append(label);
      } else {
        // Struck through like the PLP's sold-out sizes, but inert: this drawer is already Notify Me.
        chip.classList.add('quick-add__size--notify');
        chip.disabled = true;
        chip.setAttribute('aria-label', unavailableTemplate.replace('__SIZE__', size));
        chip.textContent = size;
      }
      list.append(chip);
    }

    const closeTemplate = this.querySelector('template[data-bis-sizes-close]');
    row.append(list);
    if (closeTemplate instanceof HTMLTemplateElement) row.append(closeTemplate.content.cloneNode(true));
    return row;
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} [returnFocus] - the shopper dismissed it, so focus goes back to the "+"
   */
  #closeCardSizes(card, returnFocus = false) {
    if (!card.hasAttribute('data-sizes-open')) return;
    delete card.dataset.sizesOpen;

    const row = card.querySelector('.back-in-stock__similar-sizes');
    if (row instanceof HTMLElement) row.hidden = true;

    const atcBtn = card.querySelector('[data-bis-atc]');
    atcBtn?.setAttribute('aria-expanded', 'false');
    if (returnFocus && atcBtn instanceof HTMLElement) atcBtn.focus({ preventScroll: true });
  }

  /**
   * Mobile/tablet: the product card's own quick-add bottom flyout, driven through a quick-add-component.
   * @param {HTMLElement} card
   * @param {Event} event
   */
  #openQuickAddFlyout(card, event) {
    let quickAdd = card.querySelector('quick-add-component');
    if (!quickAdd) {
      const link = card.querySelector('.back-in-stock__similar-link');
      const image = card.querySelector('.back-in-stock__similar-image');
      quickAdd = document.createElement('quick-add-component');
      quickAdd.hidden = true;
      quickAdd.dataset.productUrl = link instanceof HTMLAnchorElement ? link.href : '';
      quickAdd.dataset.productTitle = card.querySelector('.back-in-stock__similar-name')?.textContent ?? '';
      quickAdd.dataset.productImage = image instanceof HTMLImageElement ? image.currentSrc || image.src : '';
      card.append(quickAdd);
    }

    const handleClick = /** @type {{ handleClick?: (event: Event) => void }} */ (quickAdd).handleClick;
    if (typeof handleClick === 'function') handleClick(event);
  }

  /**
   * @param {1 | -1} direction
   */
  #scrollSimilarList(direction) {
    const list = this.querySelector('[data-bis-similar-list]');
    if (!(list instanceof HTMLElement)) return;

    const card = list.querySelector('.back-in-stock__similar-card');
    const gap = parseFloat(getComputedStyle(list).gap) || 0;
    const amount = card instanceof HTMLElement ? card.getBoundingClientRect().width + gap : list.clientWidth;
    list.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  #productHandleFromUrl(href) {
    try {
      const path = new URL(href, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/?#]+)/);
      return match?.[1] ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  }

  /**
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #sizedImageUrl(url, width) {
    const absolute = url.startsWith('//') ? `https:${url}` : url;
    if (/[?&]width=\d+/.test(absolute)) return absolute.replace(/([?&]width=)\d+/, `$1${width}`);
    return absolute.includes('?') ? `${absolute}&width=${width}` : `${absolute}?width=${width}`;
  }

  /** @param {Event} event */
  #onSimilarScroll = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    const direction = button.dataset.bisScroll === 'next' ? 1 : -1;
    this.#scrollSimilarList(direction);
  };

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    const { resource: variant, data } = event.detail ?? {};
    if (data?.productId && data.productId !== this.dataset.productId) return;

    this.hidden = !variant || Boolean(variant.available);
    if (!variant) return;

    const previousSize = this.dataset.variantSize || '';
    this.dataset.variantId = String(variant.id);
    this.#updateCopy(this.#readSelectedSize());
    this.#resetStatus();

    const list = this.querySelector('[data-bis-similar-list]');
    if (!(list instanceof HTMLElement) || list.dataset.loaded !== 'true' || this.hidden) return;
    if ((this.dataset.variantSize || '') === previousSize) return;
    this.#applySizeFilter(list);
  };

  /** @param {Event} event */
  handleSubmit = async (event) => {
    event.preventDefault();
    this.#resetStatus();

    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-bis-email]'));
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bis-submit]'));
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;
    const errorEl = this.querySelector('[data-bis-error]');

    if (emailInput) emailInput.value = emailInput.value.trim();
    const email = emailInput?.value ?? '';

    if (!emailInput || !email || !emailInput.checkValidity()) {
      this.#showError();
      return;
    }

    if (!productId || !variantId) {
      this.#showError(errorEl instanceof HTMLElement ? errorEl.dataset.submitError : '');
      return;
    }

    if (submitButton) submitButton.disabled = true;

    try {
      const bis = await this.#waitForBis();
      const data = await this.#createSubscription(bis, email, variantId, productId);

      if (data?.status !== 'OK') {
        const ampMessage = this.#bisErrorMessage(data);
        this.#showError(ampMessage || (errorEl instanceof HTMLElement ? errorEl.dataset.submitError : ''));
        return;
      }

      const success = this.querySelector('[data-bis-success]');
      if (success instanceof HTMLElement) {
        if (typeof data.message === 'string' && data.message.trim()) {
          success.textContent = data.message;
        }
        success.hidden = false;
      }
      emailInput.value = '';
    } catch (error) {
      const unavailable =
        error instanceof Error && error.message === 'AMP Back in Stock unavailable';
      const message = unavailable
        ? errorEl instanceof HTMLElement
          ? errorEl.dataset.unavailableError
          : ''
        : errorEl instanceof HTMLElement
          ? errorEl.dataset.submitError
          : '';
      this.#showError(message || '');
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  };

  /**
   * AMP injects window.BIS via the app embed (loads after theme scripts).
   * @param {number} [timeoutMs]
   * @returns {Promise<{ create: Function }>}
   */
  async #waitForBis(timeoutMs = 10000) {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      const bis = /** @type {{ create?: Function } | undefined} */ (window.BIS);
      if (typeof bis?.create === 'function') return /** @type {{ create: Function }} */ (bis);
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    throw new Error('AMP Back in Stock unavailable');
  }

  /**
   * @param {{ create: Function }} bis
   * @param {string} email
   * @param {string} variantId
   * @param {string} productId
   * @returns {Promise<{ status?: string, message?: string, errors?: Record<string, string[]> }>}
   */
  #createSubscription(bis, email, variantId, productId) {
    return new Promise((resolve, reject) => {
      try {
        const result = bis.create(email, Number(variantId), Number(productId));
        if (result && typeof result.then === 'function') {
          result.then(resolve);
          return;
        }
        resolve(result ?? {});
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * @param {{ message?: string, errors?: Record<string, string[]> } | null | undefined} data
   * @returns {string}
   */
  #bisErrorMessage(data) {
    if (!data) return '';
    if (data.errors && typeof data.errors === 'object') {
      const parts = Object.values(data.errors)
        .flat()
        .map((part) => (typeof part === 'string' ? part.trim() : ''))
        .filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    return '';
  }

  /** @returns {string} */
  #readSelectedSize() {
    const checked = this.closest('.shopify-section, dialog, product-card')?.querySelector(
      '.variant-option[data-is-size-option="true"] input:checked'
    );
    if (checked instanceof HTMLInputElement && checked.value) return checked.value;
    return this.dataset.variantSize ?? '';
  }

  /** @param {string} size */
  #updateCopy(size) {
    this.dataset.variantSize = size;

    const apply = (selector, template) => {
      const el = this.querySelector(selector);
      if (!(el instanceof HTMLElement) || !template) return;
      el.textContent = template.replaceAll('__SIZE__', size);
    };

    apply('[data-bis-size]', this.dataset.sizeTemplate);
    apply('[data-bis-message]', this.dataset.messageTemplate);
    apply('[data-bis-similar-title]', this.dataset.similarTemplate);
  }

  #resetStatus() {
    const success = this.querySelector('[data-bis-success]');
    const error = this.querySelector('[data-bis-error]');
    if (success instanceof HTMLElement) success.hidden = true;
    if (error instanceof HTMLElement) {
      error.hidden = true;
      if (error.dataset.defaultText) error.textContent = error.dataset.defaultText;
    }
  }

  /** @param {string} [message] */
  #showError(message) {
    const error = this.querySelector('[data-bis-error]');
    if (!(error instanceof HTMLElement)) return;
    if (!error.dataset.defaultText) {
      error.dataset.defaultText = error.textContent?.trim() || '';
    }
    error.textContent =
      typeof message === 'string' && message.trim() ? message.trim() : error.dataset.defaultText;
    error.hidden = false;
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
